import { Injectable } from '@angular/core';
import { BlocklyStorageService } from './blockly-storage.service';

/**
 * Zentrale Registry für Blockly-Variablennamen aus allen Editoren.
 * Scannt alle Blockly-XMLs des aktuellen Bots und extrahiert tatsächlich von
 * Blöcken referenzierte Variablennamen.
 * Editor-übergreifend nutzbar (Singleton-Service).
 */
@Injectable({
    providedIn: 'root'
})
export class BlocklyVariableRegistryService {
    private _variables = new Map<string, Set<string>>(); // varName -> Set<uniqueId> (Editor-Quellen)

    constructor(private blocklyStorage: BlocklyStorageService) { }

    /**
     * Scannt alle geladenen Blockly-XMLs nach verwendeten Variablen.
     */
    public refresh(): void {
        this._variables.clear();
        for (const stage of this.blocklyStorage.stages) {
            this._extractVariablesFromXml(stage.xml, stage.uniqueId);
        }
    }

    /**
     * Aktualisiert die Registry mit dem aktuellen XML eines einzelnen Editors.
     */
    public updateFromEditor(uniqueId: string, xml: string): void {
        // Entferne alte Variablen dieses Editors
        for (const [varName, sources] of this._variables.entries()) {
            sources.delete(uniqueId);
            if (sources.size === 0) {
                this._variables.delete(varName);
            }
        }
        // Extrahiere neue Variablen
        this._extractVariablesFromXml(xml, uniqueId);
    }

    /**
     * Gibt alle bekannten Variablennamen als Array zurück.
     */
    public getAllVariableNames(): string[] {
        return Array.from(this._variables.keys());
    }

    private _extractVariablesFromXml(xml: string, sourceUniqueId: string): void {
        if (!xml) return;

        const document = new DOMParser().parseFromString(xml, 'text/xml');
        if (document.getElementsByTagName('parsererror').length > 0) {
            return;
        }

        const variableNamesById = new Map<string, string>();
        for (const variable of Array.from(document.getElementsByTagName('variable'))) {
            const id = variable.getAttribute('id');
            const name = variable.textContent.trim();
            if (id && name) {
                variableNamesById.set(id, name);
            }
        }

        const usedVariableNames = new Set<string>();
        for (const field of Array.from(document.getElementsByTagName('field'))) {
            const variableId = field.getAttribute('id');
            const variableName = variableId ? variableNamesById.get(variableId) : undefined;

            if (variableName) {
                usedVariableNames.add(variableName);
            } else if (field.getAttribute('name') === 'VAR') {
                const legacyVariableName = field.textContent.trim();
                if (legacyVariableName) {
                    usedVariableNames.add(legacyVariableName);
                }
            }
        }

        for (const variableName of usedVariableNames) {
            if (!this._variables.has(variableName)) {
                this._variables.set(variableName, new Set());
            }
            this._variables.get(variableName)?.add(sourceUniqueId);
        }
    }
}
